import { useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { InputSelectField, Radio, RadioGroup, Row, TextField } from '@tempmee/react';
import { COST_UNIT_OPTIONS } from '~/constants/officeProfileOptions';
import { SectionEditDialog } from '~/components/shared/AccountArea';
import {
  buildParkingInformation,
  isAddressParkingValid,
  toSectionDefaults,
  useUpdateLocation,
} from '~/domain/LocationSettings';
import { useGetMetadata } from '~/domain/Metadata';
import { ParkingCostEnum } from '~/models/ParkingInformation';
import { toSelectOptions } from '~/utils/Enum';
import { formatCurrencyDisplay, parseCurrencyInput } from '~/utils/formatMoney';
import type { ISectionModalProps } from './types';

const PAID = 'paid';
const FREE = 'free';

/** The address, shown but not editable (it changes only at sign-up), and parking. */
export const AddressParkingModal = ({ officeId, office, onClose, onSaved }: ISectionModalProps) => {
  const parkingTypes = useGetMetadata().data?.parkingTypes;
  const defaultValues = useMemo(() => toSectionDefaults(office, null).addressParking, [office]);
  const { control, handleSubmit, formState } = useForm({ defaultValues, mode: 'onChange' });
  const values = useWatch({ control });
  const updateLocation = useUpdateLocation(officeId);

  // Every field here is a parking field, so any edit makes the whole answer required (`useParking.js`).
  const canSave =
    formState.isDirty &&
    isAddressParkingValid({
      isParkingFree: values.isParkingFree,
      parkingCost: values.parkingCost ?? '',
      parkingCostType: values.parkingCostType ?? ParkingCostEnum.Hourly,
      parkingType: values.parkingType ?? '',
    });

  const save = handleSubmit(async (submitted) => {
    await updateLocation.mutateAsync({ parkingInformation: buildParkingInformation(submitted) });
    onSaved();
  });

  let parkingAnswer = '';
  if (values.isParkingFree === true) parkingAnswer = FREE;
  if (values.isParkingFree === false) parkingAnswer = PAID;

  return (
    <SectionEditDialog
      title="Edit Address & Parking"
      dataCy="edit-address-parking"
      onClose={onClose}
      onSave={save}
      canSave={canSave}
    >
      <TextField
        label="Address"
        value={office.address?.[0]?.formatted_address ?? ''}
        disabled
        readOnly
        data-cy="location-address"
      />
      <Controller
        name="isParkingFree"
        control={control}
        render={({ field }) => (
          <RadioGroup
            label="Parking Cost"
            value={parkingAnswer}
            onChange={(answer) => field.onChange(answer === FREE)}
            orientation="horizontal"
          >
            <Radio value={PAID} label="Paid" data-cy="parking-paid" />
            <Radio value={FREE} label="Free" data-cy="parking-free" />
          </RadioGroup>
        )}
      />
      {values.isParkingFree === false && (
        <Row gap={2} data-cy="parking-cost-fields">
          <Controller
            name="parkingCost"
            control={control}
            render={({ field }) => (
              <TextField
                label="Amount"
                placeholder="US$ --.--"
                value={formatCurrencyDisplay(field.value)}
                onChange={(event) => {
                  const parsed = parseCurrencyInput(event.target.value);
                  if (parsed !== null) field.onChange(parsed);
                }}
                data-cy="parking-cost"
              />
            )}
          />
          <Controller
            name="parkingCostType"
            control={control}
            render={({ field }) => (
              <InputSelectField
                label="Per"
                options={COST_UNIT_OPTIONS}
                value={field.value}
                onChange={(unit) => field.onChange(unit as ParkingCostEnum)}
              />
            )}
          />
        </Row>
      )}
      <Controller
        name="parkingType"
        control={control}
        render={({ field }) => (
          <InputSelectField
            label="Parking type"
            options={toSelectOptions(parkingTypes ?? [])}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />
    </SectionEditDialog>
  );
};
